import { Component } from '@theme/component';

class LanoelCampaignSlider extends Component {
  #abort;
  #timer;
  #observer;
  #motion;
  #index = 0;
  #manualPause = false;
  #hover = false;
  #visible = true;
  #scrollTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
    clearTimeout(this.#scrollTimer);
    super.disconnectedCallback();
  }

  #bind() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    const track = this.querySelector('[data-campaign-track]');
    if (!track) return;
    this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#manualPause = this.#motion.matches;
    this.addEventListener('mouseenter', () => { this.#hover = true; this.#schedule(); }, options);
    this.addEventListener('mouseleave', () => { this.#hover = false; this.#schedule(); }, options);
    this.addEventListener('focusin', (event) => { if (!event.target.closest('[data-campaign-pause]')) this.#manualPause = true; this.#schedule(); }, options);
    this.addEventListener('focusout', () => queueMicrotask(() => this.#schedule()), options);
    this.addEventListener('pointerdown', (event) => { if (!event.target.closest('[data-campaign-pause]')) this.#manualPause = true; this.#schedule(); }, options);
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-campaign-pause')) {
        this.#manualPause = button.getAttribute('aria-pressed') !== 'true';
        this.#schedule();
      }
      if (button.hasAttribute('data-campaign-to')) this.#go(Number(button.dataset.campaignTo), true);
      if (button.hasAttribute('data-campaign-step')) this.#go(this.#index + Number(button.dataset.campaignStep), true);
    }, options);
    track.addEventListener('keydown', (event) => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.#manualPause = true;
      this.#go(this.#index + (event.key === 'ArrowRight' ? 1 : -1), true);
    }, options);
    track.addEventListener('scroll', () => {
      clearTimeout(this.#scrollTimer);
      this.#scrollTimer = setTimeout(() => {
        this.#index = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
        this.#sync();
        this.#schedule();
      }, 100);
    }, { ...options, passive: true });
    document.addEventListener('visibilitychange', () => this.#schedule(), options);
    this.#motion.addEventListener('change', () => {
      if (this.#motion.matches) this.#manualPause = true;
      this.#schedule();
    }, options);
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#schedule();
    }, { threshold: 0.15 });
    this.#observer.observe(this);
    this.#index = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
    this.#sync();
    this.#schedule();
  }

  #go(index, manual = false) {
    const track = this.querySelector('[data-campaign-track]');
    const slides = this.querySelectorAll('[data-campaign-slide]');
    this.#index = (index + slides.length) % slides.length;
    track.scrollTo({ left: this.#index * track.clientWidth, behavior: this.#motion.matches ? 'instant' : 'smooth' });
    this.#sync();
    if (manual) {
      const status = this.querySelector('[data-campaign-status]');
      if (status) status.textContent = `${this.#index + 1} / ${slides.length}`;
    }
    this.#schedule();
  }

  #sync() {
    this.querySelectorAll('[data-campaign-slide]').forEach((slide, index) => {
      const active = index === this.#index;
      slide.inert = !active;
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.querySelectorAll('[data-campaign-to]').forEach((button, index) => button.setAttribute('aria-current', index === this.#index ? 'true' : 'false'));
    this.dataset.activeSlide = String(this.#index + 1);
  }

  #schedule() {
    clearTimeout(this.#timer);
    const paused = this.#manualPause || this.#motion?.matches;
    const button = this.querySelector('[data-campaign-pause]');
    if (button) {
      button.setAttribute('aria-pressed', String(paused));
      button.setAttribute('aria-label', paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      button.dataset.paused = String(paused);
    }
    if (paused || this.#hover || this.contains(document.activeElement) || !this.#visible || document.hidden || window.Shopify?.designMode) return;
    if (this.querySelectorAll('[data-campaign-slide]').length < 2) return;
    this.#timer = setTimeout(() => this.#go(this.#index + 1), Number(this.dataset.interval) || 7000);
  }
}

if (!customElements.get('lanoel-campaign-slider')) customElements.define('lanoel-campaign-slider', LanoelCampaignSlider);

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-newsletter-open]');
  const dialog = document.getElementById('lanoel-newsletter-offer');
  if (opener && dialog) { event.preventDefault(); dialog.showModal(); }
  const signup = event.target.closest('[data-klaviyo-open]');
  if (signup) {
    dialog?.close();
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', signup.dataset.klaviyoOpen]);
  }
  if (event.target === dialog) dialog.close();
});

if (new URLSearchParams(location.search).has('cro_qa')) {
  let shift = 0;
  try {
    new PerformanceObserver((list) => {
      const entry = list.getEntries().at(-1);
      if (entry) document.documentElement.dataset.croLcp = String(Math.round(entry.startTime));
    }).observe({ type: 'largest-contentful-paint', buffered: true });
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) if (!entry.hadRecentInput) shift += entry.value;
      document.documentElement.dataset.croCls = shift.toFixed(4);
    }).observe({ type: 'layout-shift', buffered: true });
  } catch {}
}
